/**
 * Inbox Page - User dashboard with pending tasks
 * 
 * Features:
 * - Display inbox tasks with priority levels
 * - Filter tasks by status (pending, completed, dismissed)
 * - Dismiss tasks without removing them
 * - Search/filter bar for easy navigation
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AdminPageLayout, 
  PageHeader, 
  Card, 
  Button, 
  LoadingState,
  Section,
  FilterBar, 
  Listing,
  Grid,
  Col
} from '../components';
import { AttributeStack, AttributeGrid } from '../components/admin/patterns/AttributePatterns';
import { useAuth } from '../contexts/AuthContext';
import { useInbox } from '../contexts/InboxContext';
import { useToast } from '../contexts/ToastContext';
import apiService from '../services/api';
import { CheckCircle, X, User, Calendar, Mail, Shield } from 'lucide-react';

const Inbox = () => {
  const [allTasks, setAllTasks] = useState([]);
  const [displayedTasks, setDisplayedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState({}); // Track tasks being dismissed
  const [completing, setCompleting] = useState({}); // Track tasks being completed
  const [statusFilter, setStatusFilter] = useState('pending'); // Default filter
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { refreshInbox } = useInbox();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  // Redirect if not authenticated (but only after auth check is complete)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
      return;
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadInboxTasks();
    }
  }, [isAuthenticated, authLoading]);

  // Update displayed tasks when filter or search changes
  useEffect(() => {
    filterAndSearchTasks();
  }, [allTasks, statusFilter, searchQuery]);

  const loadInboxTasks = async () => {
    try {
      setLoading(true);
      const response = await apiService.getInboxTasks();
      let allTasksData = response.tasks || [];

      // Transform backend tasks to frontend format
      const transformedTasks = allTasksData.map(task => ({
        id: task.id,
        title: getTaskTitle(task),
        description: getTaskDescription(task),
        type: task.task_type,
        priority: task.priority || 'normal',
        due_date: task.metadata?.due_date || null,
        status: task.status,
        url: task.action_url || getTaskUrl(task),
        metadata: task.metadata,
        relatedOfferId: task.related_offer_id
      }));

      setAllTasks(transformedTasks);
      setPendingCount(response.pending_count || 0);
    } catch (error) {
      console.error('Failed to load inbox tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSearchTasks = () => {
    let filtered = allTasks;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(query) ||
        task.description.toLowerCase().includes(query) ||
        task.type.toLowerCase().includes(query)
      );
    }

    setDisplayedTasks(filtered);
  };

  // Helper to get task title based on task type
  const getTaskTitle = (task) => {
    switch (task.task_type) {
      case 'complete_registration':
        return 'Complete Registration';
      case 'complete_team_enrollment':
        return `Team Enrollment: ${task.metadata?.team_name || 'Team'}`;
      case 'upload_document':
        return `Upload Document: ${task.metadata?.document_name || 'Required Document'}`;
      case 'complete_document_requirement':
        return `Document Required: ${task.metadata?.requirement_title || 'Action Needed'}`;
      default:
        return task.metadata?.title || 'Action Required';
    }
  };

  // Helper to get task description based on task type
  const getTaskDescription = (task) => {
    switch (task.task_type) {
      case 'complete_registration':
        return 'Finish setting up your family profile to access all features';
      case 'complete_team_enrollment':
        return `Complete enrollment for ${task.metadata?.team_name || 'your team'}`;
      case 'upload_document':
        return `Upload required document: ${task.metadata?.document_name || 'Document'}`;
      case 'complete_document_requirement':
        return `Please review and sign the required document: ${task.metadata?.requirement_title || 'Document'}`;
      default:
        return task.metadata?.description || 'Please complete this task';
    }
  };

  // Helper to get task URL based on task type
  const getTaskUrl = (task) => {
    switch (task.task_type) {
      case 'complete_registration':
        return '/profile';
      case 'complete_team_enrollment':
        return task.action_url || '/teams';
      case 'upload_document':
        return '/documents';
      default:
        return '/';
    }
  };

  const handleTaskAction = (task) => {
    // Navigate based on task URL if provided
    if (task.url) {
      // Check for empty or invalid URLs
      if (task.url.trim() === '' || task.url === '/') {
        showError('This task cannot be completed at this time. Please contact support.');
        return;
      }

      // If it's an absolute URL, check if it's external or internal
      try {
        const url = new URL(task.url, window.location.origin);
        if (url.origin === window.location.origin) {
          // Internal absolute URL, use the pathname and search for navigation
          navigate(url.pathname + url.search);
        } else {
          // External URL, open in new tab or redirect
          window.location.href = task.url;
        }
      } catch (e) {
        // Not a full URL or relative path, just navigate to it
        navigate(task.url);
      }
      return;
    }

    // Fallback to task ID-based navigation
    const taskId = task.id?.toLowerCase();
    if (taskId === 'complete_registration') {
      navigate('/register');
    } else if (taskId === 'document_requirements') {
      navigate('/documents');
    } else if (taskId === 'add_player') {
      navigate('/register');
    } else if (taskId === 'skills_assessment') {
      navigate('/profile');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      setCompleting(prev => ({ ...prev, [taskId]: true }));
      await apiService.completeInboxTask(taskId);
      showSuccess('Task completed successfully');
      // Reload tasks to update status and refresh inbox badge count
      await loadInboxTasks();
      await refreshInbox();
    } catch (error) {
      console.error('Failed to complete task:', error);
      showError('Failed to complete task');
    } finally {
      setCompleting(prev => ({ ...prev, [taskId]: false }));
    }
  };

  const handleDismissTask = async (taskId) => {
    try {
      setDismissing(prev => ({ ...prev, [taskId]: true }));
      await apiService.dismissInboxTask(taskId);
      showSuccess('Task dismissed');
      // Reload tasks to update status and refresh inbox badge count
      await loadInboxTasks();
      await refreshInbox();
    } catch (error) {
      console.error('Failed to dismiss task:', error);
      showError('Failed to dismiss task');
    } finally {
      setDismissing(prev => ({ ...prev, [taskId]: false }));
    }
  };

  const getTaskPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return '#dc2626'; // Red for urgent
      case 'high': return '#ea580c'; // Orange-red for high
      case 'normal': return '#2563eb'; // Blue for normal
      case 'low': return '#64748b'; // Gray for low
      default: return 'var(--color-text-secondary)';
    }
  };

  const getTaskPriorityLabel = (priority) => {
    switch (priority) {
      case 'urgent': return 'URGENT';
      case 'high': return 'HIGH PRIORITY';
      case 'normal': return 'NORMAL';
      case 'low': return 'LOW PRIORITY';
      default: return priority?.toUpperCase() || 'NORMAL';
    }
  };

  // Don't render if auth is still loading or not authenticated
  if (authLoading) {
    return (
      <AdminPageLayout maxWidth="1400px">
        <LoadingState />
      </AdminPageLayout>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <AdminPageLayout maxWidth="1400px">
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            Loading your inbox...
          </p>
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout maxWidth="1400px">
      <PageHeader
        title="INBOX"
        subtitle="Manage your tasks and notifications"
        style={{ marginBottom: '2rem' }}
      />


      {/* Account Status Section */}
      <Section title="ACCOUNT STATUS">
        <Card level={1}>
          <AttributeGrid columns="4" gap="2.5rem">
            <AttributeStack 
              label="Email" 
              value={user?.email || 'Not Available'}
              icon={<Mail size={16} />}
            />
            <AttributeStack 
              label="Account Status" 
              value={user?.account_status?.replace('_', ' ')?.toUpperCase() || 'ACTIVE'}
              icon={<Shield size={16} />}
            />
            <AttributeStack 
              label="Member Since" 
              value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              icon={<Calendar size={16} />}
            />
            <AttributeStack 
              label="User Type" 
              value={user?.user_type?.toUpperCase() || 'PARENT'}
              icon={<User size={16} />}
            />
          </AttributeGrid>
        </Card>
      </Section>

      {/* Tasks Section */}
      <Section 
        title="TASKS"
        actions={
          <div style={{ 
            background: '#dc2626', 
            color: 'white', 
            padding: '0.25rem 0.75rem', 
            borderRadius: '20px', 
            fontSize: '0.85rem', 
            fontWeight: 600 
          }}>
            {pendingCount} PENDING
          </div>
        }
      >
        <Card level={1}>
          {/* Filter and Search Bar */}
          <div style={{ marginBottom: '1.5rem' }}>
            <FilterBar
          filters={[
            {
              type: 'select',
              label: 'STATUS',
              name: 'status',
              value: statusFilter,
              onChange: (e) => setStatusFilter(e.target.value),
              options: [
                { value: 'pending', label: 'Pending' },
                { value: 'dismissed', label: 'Dismissed' },
                { value: 'completed', label: 'Completed' },
                { value: 'all', label: 'All Tasks' }
              ]
            },
            {
              type: 'search',
              label: 'SEARCH',
              name: 'search',
              placeholder: 'Search tasks...',
              value: searchQuery,
              onChange: (e) => setSearchQuery(e.target.value)
            }
          ]}
          onClear={() => {
            setStatusFilter('pending');
            setSearchQuery('');
          }}
            />
          </div>

          <Listing
            items={displayedTasks}
            emptyTitle={searchQuery ? 'NO MATCHING TASKS' : 'ALL CAUGHT UP!'}
            emptyMessage={searchQuery
              ? `No tasks match "${searchQuery}"`
              : 'You have no pending tasks at the moment.'}
            emptyIcon={<CheckCircle size={48} />}
            renderItem={(task) => (
              <Card key={task.id} level={2}>
                <div style={{ 
                  padding: '1.5rem', 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'stretch', 
                  gap: '1.5rem',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ flex: 1, minWidth: '300px' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.75rem', 
                      flexWrap: 'wrap',
                      marginBottom: '0.75rem'
                    }}>
                      <h3 style={{ 
                        color: 'var(--color-primary)', 
                        margin: 0, 
                        fontSize: '1.1rem',
                        fontWeight: 600
                      }}>
                        {task.title}
                      </h3>
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        color: 'white',
                        background: getTaskPriorityColor(task.priority)
                      }}>
                        {getTaskPriorityLabel(task.priority)}
                      </span>
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        background: 'var(--color-text-tertiary)',
                        color: 'white'
                      }}>
                        {task.status === 'pending' ? 'PENDING' : task.status.toUpperCase()}
                      </span>
                    </div>
                    <p style={{ 
                      color: 'var(--color-text-secondary)', 
                      marginBottom: 0, 
                      fontSize: '0.95rem', 
                      lineHeight: 1.6 
                    }}>
                      {task.description}
                    </p>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.75rem', 
                    flexShrink: 0 
                  }}>
                    {task.status === 'pending' && (
                      <>
                        <Button
                          variant="primary"
                          onClick={() => handleTaskAction(task)}
                          disabled={completing[task.id] || dismissing[task.id]}
                        >
                          {completing[task.id] ? 'COMPLETING...' : 'CONTINUE'}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => handleDismissTask(task.id)}
                          disabled={dismissing[task.id] || completing[task.id]}
                          title="Dismiss task (it will still show in your inbox)"
                          style={{ minWidth: '100px' }}
                        >
                          {dismissing[task.id] ? 'DISMISSING...' : 'DISMISS'}
                        </Button>
                      </>
                    )}
                    {task.status === 'dismissed' && (
                      <div style={{
                        padding: '0.6rem 1.2rem',
                        background: 'var(--color-text-tertiary)',
                        color: 'white',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        textAlign: 'center',
                        whiteSpace: 'nowrap'
                      }}>
                        TASK DISMISSED
                      </div>
                    )}
                    {task.status === 'completed' && (
                      <div style={{
                        padding: '0.6rem 1.2rem',
                        background: 'rgba(34, 197, 94, 0.1)',
                        color: '#22c55e',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        textAlign: 'center',
                        whiteSpace: 'nowrap'
                      }}>
                        TASK COMPLETED
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}
          />
        </Card>
      </Section>
    </AdminPageLayout>
  );
};

export default Inbox;
